/** ScopePicker — connector privacy-scope picker.
 *
 *  Lets the user pick the privacy scope (private / personal / family /
 *  social / work / business) at connector setup time. Sets the
 *  `privacy_scope` column on the work_connectors row.
 *
 *  ScopeBadge renders a compact pill for the chosen scope wherever a
 *  paired-connector header / row appears.
 *
 *  Minimal hand-rolled component — no router or third-party deps. */
import { useState, type FormEvent } from "react";
import { Lock, Briefcase, Building2, Heart, Users, User, DollarSign } from "lucide-react";
import { scopeChipMeta } from "../lib/scope-meta";
import { ScopeOption } from "./ScopeOption";

// Canonical multi-tag model (Step 1.5): primary = personal | work
// (connector-set, exactly one); additional = family | financial |
// business | private. `social` is retained only so legacy rows /
// other call sites keep type-checking; it renders as a plain pill.
export type Scope =
  | "private"
  | "personal"
  | "family"
  | "financial"
  | "social"
  | "work"
  | "business";

export const PRIMARY_SCOPES: Scope[] = ["personal", "work"];

const SCOPE_OPTIONS: Array<{
  id: Scope;
  label: string;
  hint: string;
  Icon: typeof Lock;
  className: string;
}> = [
  {
    id: "private",
    label: "Private",
    hint: "Stays on this device. Local-only AI.",
    Icon: Lock,
    className: "border-slate-300 dark:border-slate-700 hover:border-slate-500",
  },
  {
    id: "personal",
    label: "Personal",
    hint: "Friends, hobbies, errands. Cloud AI ok.",
    Icon: User,
    className: "border-violet-300 dark:border-violet-700 hover:border-violet-500",
  },
  {
    id: "family",
    label: "Family",
    hint: "Spouse, kids, parents. Cloud AI ok.",
    Icon: Heart,
    className: "border-rose-300 dark:border-rose-700 hover:border-rose-500",
  },
  {
    id: "social",
    label: "Social",
    hint: "Wider circle, communities. Cloud AI ok.",
    Icon: Users,
    className: "border-sky-300 dark:border-sky-700 hover:border-sky-500",
  },
  {
    id: "financial",
    label: "Financial",
    hint: "Money, bills, transactions. Cloud AI ok.",
    Icon: DollarSign,
    className: "border-teal-300 dark:border-teal-700 hover:border-teal-500",
  },
  {
    id: "work",
    label: "Work",
    hint: "Job + colleagues. Cloud AI ok.",
    Icon: Briefcase,
    className: "border-amber-300 dark:border-amber-700 hover:border-amber-500",
  },
  {
    id: "business",
    label: "Business",
    hint: "Customers, vendors, deals. Cloud AI ok.",
    Icon: Building2,
    className: "border-emerald-300 dark:border-emerald-700 hover:border-emerald-500",
  },
];

const BADGE_TINT: Record<Scope, string> = {
  private: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  personal: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200",
  family: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
  financial: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200",
  social: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200",
  work: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  business: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
};

export function ScopeBadge({ scope }: { scope: string }) {
  const s = (SCOPE_OPTIONS.find((o) => o.id === scope) ?? null);
  const tint = BADGE_TINT[scope as Scope] ?? BADGE_TINT.private;
  const Icon = s?.Icon ?? Lock;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${tint}`}
      title={s?.hint ?? scope}
    >
      <Icon className="w-3 h-3" />
      {s?.label ?? scope}
    </span>
  );
}

/** Normalize the various shapes the API hands back (a CSV from
 *  group_concat, an array, or a single string) into a scope list. */
export function toScopeList(scopes: string | string[] | null | undefined): string[] {
  if (!scopes) return [];
  const arr = Array.isArray(scopes) ? scopes : String(scopes).split(",");
  return [...new Set(arr.map((s) => s.trim()).filter(Boolean))];
}

/** Multi-tag display: the primary (personal|work) renders as the
 *  dominant, color-coded chip; every additional tag renders as a
 *  small pill. Hover shows the full set. */
export function ScopeChips({
  scopes,
  className = "",
}: {
  scopes: string | string[] | null | undefined;
  className?: string;
}) {
  const list = toScopeList(scopes);
  if (list.length === 0) return null;
  const isPrivate = list.includes("private");
  const primary = isPrivate
    ? "private"
    : (list.find((s) => PRIMARY_SCOPES.includes(s as Scope)) ?? "personal");
  const additional = list.filter(
    (s) => s !== primary && s !== "private",
  );
  // The primary . first-sub renders through the shared scopeChipMeta
  // palette so connector chips match every packet surface. Any extra
  // tags trail as muted pills; hover shows the full set.
  const sub = primary === "private" ? null : (additional[0] ?? null);
  const meta = scopeChipMeta(primary, sub);
  const rest = sub ? additional.slice(1) : additional;
  const title = list.join(" · ");
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} title={title}>
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${meta.className}`}
      >
        {meta.label}
      </span>
      {rest.map((s) => (
        <span
          key={s}
          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        >
          {s}
        </span>
      ))}
    </span>
  );
}

export function ScopePicker({
  subject,
  busy,
  error,
  onSubmit,
  onChange,
  submitLabel,
  defaultScope,
  primaryOnly = false,
}: {
  subject: string;
  busy: boolean;
  error: string | null;
  onSubmit: (scope: Scope) => Promise<void> | void;
  /** Fires whenever the selection changes (before submit) so a host
   *  can render dependent UI such as a sub-category dropdown. */
  onChange?: (scope: Scope) => void;
  submitLabel?: string;
  defaultScope?: Scope;
  /** Connector-instance picker: only the two primaries are user-set
   *  (additional tags emerge from classifiers per-packet). */
  primaryOnly?: boolean;
}) {
  const [scope, setScope] = useState<Scope>(defaultScope ?? "personal");
  const options = primaryOnly
    ? SCOPE_OPTIONS.filter((o) => PRIMARY_SCOPES.includes(o.id))
    : SCOPE_OPTIONS;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void onSubmit(scope);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-5 sm:p-6 space-y-4"
    >
      <header>
        <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
          Pick a privacy scope
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          What kind of content does {subject} carry? Memso uses this to
          route AI per-scope and surface the right contacts.
        </p>
      </header>
      <ul
        role="radiogroup"
        aria-label="Privacy scope"
        className="grid grid-cols-1 sm:grid-cols-2 gap-2"
      >
        {options.map((o) => {
          const active = scope === o.id;
          return (
            <li key={o.id}>
              <ScopeOption
                selected={active}
                onSelect={() => {
                  setScope(o.id);
                  onChange?.(o.id);
                }}
                label={o.label}
                hint={o.hint}
                Icon={o.Icon}
                className="w-full"
              />
            </li>
          );
        })}
      </ul>
      {error && (
        <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-violet-600 text-white shadow-sm hover:bg-violet-700 disabled:opacity-60"
        >
          {busy ? "Saving…" : submitLabel ?? "Save scope"}
        </button>
      </div>
    </form>
  );
}
